import React from 'react';
import { observer } from 'mobx-react';
import { BasicTab, Spinner } from 'ui-lib';
import fileTransferCss from './new-file-transfer.css';
import { useAccountStore, useAuthStore, useStores } from 'store';
import { useDeviceGroupCommonStates } from '../device-groups';
import { useAccountTreeSelectDataSource } from 'pages/private/admin/accounts';
import { OrganisationSelect } from 'pages/private/components/organisation-select';
import { AccountType } from 'dto/access-management/account-dto';
import { TransferFilesTab } from './transfer-files-tab';
import { TrackingTab } from './tracking-tab';
import { UploadFilesTab } from './upload-files-tab';
import { useUrlSearch } from 'hooks/use-search.hook';

export const NewFileTransfer = observer(() => {
    const { currentUser } = useAuthStore();
    const accountStore = useAccountStore();
    const stores = useStores();
    const selectAccountSource = useAccountTreeSelectDataSource({ selectableTypes: [AccountType.CLIENT] });
    const { getSearchParam, updateSearchParams } = useUrlSearch();
    const paramAccountUuid = getSearchParam('accountUuid') || null;
    const [selectedClientAccountId, setSelectedClientAccountId] = React.useState<number>(() => {
        if (currentUser.accountType === AccountType.CLIENT) {
            return currentUser.accountId;
        } else if (!paramAccountUuid) {
            return null;
        }
        const paramAccount = accountStore.getAccountByUuid(paramAccountUuid);
        return paramAccount?.accountType === AccountType.CLIENT ? paramAccount.id : null;
    });
    const [lockOrganisationSelect, setLockOrganisationSelect] = React.useState<boolean>(false);

    const {
        treeSelectableDataSource: deviceGroupsTree,
        deviceGroupLoading,
        onAccountChanged,
    } = useDeviceGroupCommonStates();

    React.useEffect(() => {
        onAccountChanged(selectedClientAccountId);
    }, [selectedClientAccountId]);

    const deviceGroupsDataSource = React.useMemo(() => {
        if (deviceGroupsTree && deviceGroupsTree.length > 0) {
            return deviceGroupsTree[0].subTreeDataNode ?? undefined;
        }
        return undefined;
    }, [deviceGroupsTree]);

    return (
        <React.Fragment>
            <Spinner spinning={deviceGroupLoading} className={fileTransferCss.spinner}></Spinner>
            {currentUser.accountType != AccountType.CLIENT && (
                <OrganisationSelect
                    placeholder='Organisation'
                    treeDataSource={selectAccountSource}
                    expandAll={true}
                    value={selectedClientAccountId}
                    onChange={(value) => {
                        setSelectedClientAccountId(value);
                    }}
                    disabled={lockOrganisationSelect}
                />
            )}
            <BasicTab
                activeKey={getSearchParam('tab') ?? 'uploadFiles'}
                defaultActiveIndex={'uploadFiles'}
                onChange={(activeKey) => {
                    updateSearchParams({
                        tab: activeKey,
                        accountUuid: accountStore.getAccountById(selectedClientAccountId)?.uuid,
                        deviceGroup: null,
                    });
                }}
                tabPanes={[
                    {
                        label: 'Upload Files',
                        key: 'uploadFiles',
                        pane: <UploadFilesTab
                            selectedClientAccountId={selectedClientAccountId}
                            deviceGroupsDataSource={deviceGroupsDataSource}
                            deviceGroupLoading={deviceGroupLoading}
                            lockOrganisationSelect={(lock: boolean) => setLockOrganisationSelect(lock)}
                        />,
                    },
                    {
                        label: 'Transfer Files',
                        key: 'transferFiles',
                        pane: <TransferFilesTab
                            selectedClientAccountId={selectedClientAccountId}
                            deviceGroupsDataSource={deviceGroupsDataSource}
                            deviceGroupLoading={deviceGroupLoading}
                            lockOrganisationSelect={(lock: boolean) => setLockOrganisationSelect(lock)}
                        />,
                    },
                    {
                        label: 'Tracking',
                        key: 'tracking',
                        pane: <TrackingTab
                            selectedClientAccountId={selectedClientAccountId}
                            deviceGroupsDataSource={deviceGroupsDataSource}
                            deviceGroupLoading={deviceGroupLoading}
                        />,
                    },
                ]}
            />
        </React.Fragment>
    );
});

